export enum NotificationPriority {
  CRITICAL = 'CRITICAL',
  WARNING = 'WARNING',
  INFO = 'INFO',
}

export enum NotificationCategory {
  BILLING = 'BILLING',
  ORDERS = 'ORDERS',
  SHIPPING = 'SHIPPING',
  INVENTORY = 'INVENTORY',
  SYSTEM = 'SYSTEM',
}

export enum NotificationType {
  // Critical
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
  EMAIL_VERIFICATION_REQUIRED = 'EMAIL_VERIFICATION_REQUIRED',
  PHONE_VERIFICATION_REQUIRED = 'PHONE_VERIFICATION_REQUIRED',
  BATCH_FAILED = 'BATCH_FAILED',
  STORE_IMPORT_FAILED = 'STORE_IMPORT_FAILED',
  // Warning
  TRIAL_ENDING_SOON = 'TRIAL_ENDING_SOON',
  SHIPMENT_LIMIT_APPROACHING = 'SHIPMENT_LIMIT_APPROACHING',
  PENDING_CANCELLATION = 'PENDING_CANCELLATION',
  TAX_ID_MISSING = 'TAX_ID_MISSING',
  SPENDING_LIMIT_WARNING = 'SPENDING_LIMIT_WARNING',
  USPS_NSA_ONBOARDING = 'USPS_NSA_ONBOARDING',
  USPS_NSA_WAITING = 'USPS_NSA_WAITING',
  API_KEYS_EXPIRING = 'API_KEYS_EXPIRING',
  INSURANCE_COVERAGE_WARNING = 'INSURANCE_COVERAGE_WARNING',
  STORE_CONNECTION_LOST = 'STORE_CONNECTION_LOST',
  STORE_IMPORT_PARTIAL = 'STORE_IMPORT_PARTIAL',
  // Info
  BATCH_COMPLETED = 'BATCH_COMPLETED',
  ORDERS_IMPORTED = 'ORDERS_IMPORTED',
  EXPORT_COMPLETED = 'EXPORT_COMPLETED',
  INVENTORY_SYNCED = 'INVENTORY_SYNCED',
  LABELS_CREATED = 'LABELS_CREATED',
  LABELS_PRINTED = 'LABELS_PRINTED',
  STORE_CONNECTED = 'STORE_CONNECTED',
  SETTINGS_SAVED = 'SETTINGS_SAVED',
  // Import
  CSV_IMPORT_COMPLETED = 'CSV_IMPORT_COMPLETED',
  CSV_IMPORT_COMPLETED_WITH_WARNINGS = 'CSV_IMPORT_COMPLETED_WITH_WARNINGS',
  CSV_IMPORT_FAILED = 'CSV_IMPORT_FAILED',
  CUSTOMERS_IMPORTED = 'CUSTOMERS_IMPORTED',
  CUSTOMERS_IMPORT_FAILED = 'CUSTOMERS_IMPORT_FAILED',
  PRODUCT_IMPORT_COMPLETED = 'PRODUCT_IMPORT_COMPLETED',
  PRODUCT_ALIAS_IMPORT_COMPLETED = 'PRODUCT_ALIAS_IMPORT_COMPLETED',
  PRODUCT_BUNDLES_IMPORT_COMPLETED = 'PRODUCT_BUNDLES_IMPORT_COMPLETED',
  PRODUCT_IMPORT_FAILED = 'PRODUCT_IMPORT_FAILED',
  INVENTORY_IMPORT_COMPLETED = 'INVENTORY_IMPORT_COMPLETED',
  INVENTORY_IMPORT_FAILED = 'INVENTORY_IMPORT_FAILED',
  // Order Alerts
  ORDER_ALERT_COMBINE_ORDERS = 'ORDER_ALERT_COMBINE_ORDERS',
  ORDER_ALERT_NEW_PRODUCTS = 'ORDER_ALERT_NEW_PRODUCTS',
  ORDER_ALERT_LOW_INVENTORY = 'ORDER_ALERT_LOW_INVENTORY',
  ORDER_ALERT_AUTOMATION_RULE = 'ORDER_ALERT_AUTOMATION_RULE',
  ORDER_ALERT_CONFIGURE_STORE = 'ORDER_ALERT_CONFIGURE_STORE',
  ORDER_ALERT_ORDERS_DEALLOCATED = 'ORDER_ALERT_ORDERS_DEALLOCATED',
  ORDER_ALERT_REJECTED_FULFILLMENT = 'ORDER_ALERT_REJECTED_FULFILLMENT',
  ORDER_ALERT_LOCAL_DELIVERY_EXCEPTION = 'ORDER_ALERT_LOCAL_DELIVERY_EXCEPTION',
  // Export
  ORDERS_EXPORT_STARTED = 'ORDERS_EXPORT_STARTED',
  ORDERS_EXPORT_COMPLETED = 'ORDERS_EXPORT_COMPLETED',
  ORDERS_EXPORT_FAILED = 'ORDERS_EXPORT_FAILED',
  SHIPMENTS_EXPORT_STARTED = 'SHIPMENTS_EXPORT_STARTED',
  SHIPMENTS_EXPORT_COMPLETED = 'SHIPMENTS_EXPORT_COMPLETED',
  SHIPMENTS_EXPORT_FAILED = 'SHIPMENTS_EXPORT_FAILED',
  FREIGHT_EXPORT_STARTED = 'FREIGHT_EXPORT_STARTED',
  FREIGHT_EXPORT_COMPLETED = 'FREIGHT_EXPORT_COMPLETED',
  FREIGHT_EXPORT_FAILED = 'FREIGHT_EXPORT_FAILED',
  PRODUCT_EXPORT_COMPLETED = 'PRODUCT_EXPORT_COMPLETED',
  PRODUCT_EXPORT_FAILED = 'PRODUCT_EXPORT_FAILED',
  INVENTORY_EXPORT_COMPLETED = 'INVENTORY_EXPORT_COMPLETED',
  INVENTORY_EXPORT_FAILED = 'INVENTORY_EXPORT_FAILED',
  LOCATIONS_EXPORT_STARTED = 'LOCATIONS_EXPORT_STARTED',
  LOCATIONS_EXPORT_COMPLETED = 'LOCATIONS_EXPORT_COMPLETED',
  LOCATIONS_EXPORT_FAILED = 'LOCATIONS_EXPORT_FAILED',
  TOTES_EXPORT_STARTED = 'TOTES_EXPORT_STARTED',
  TOTES_EXPORT_COMPLETED = 'TOTES_EXPORT_COMPLETED',
  TOTES_EXPORT_FAILED = 'TOTES_EXPORT_FAILED',
  CUSTOM_PACKAGES_EXPORT_STARTED = 'CUSTOM_PACKAGES_EXPORT_STARTED',
  CUSTOM_PACKAGES_EXPORT_COMPLETED = 'CUSTOM_PACKAGES_EXPORT_COMPLETED',
  CUSTOM_PACKAGES_EXPORT_FAILED = 'CUSTOM_PACKAGES_EXPORT_FAILED',
  FORECASTING_EXPORT_FAILED = 'FORECASTING_EXPORT_FAILED',
  FULFILLMENTS_EXPORT_STARTED = 'FULFILLMENTS_EXPORT_STARTED',
  FULFILLMENTS_EXPORT_COMPLETED = 'FULFILLMENTS_EXPORT_COMPLETED',
  FULFILLMENTS_EXPORT_FAILED = 'FULFILLMENTS_EXPORT_FAILED',
  TRANSACTION_HISTORY_EXPORT_STARTED = 'TRANSACTION_HISTORY_EXPORT_STARTED',
  TRANSACTION_HISTORY_EXPORT_COMPLETED = 'TRANSACTION_HISTORY_EXPORT_COMPLETED',
  TRANSACTION_HISTORY_EXPORT_FAILED = 'TRANSACTION_HISTORY_EXPORT_FAILED',
  RETURNS_EXPORT_COMPLETED = 'RETURNS_EXPORT_COMPLETED',
}

export enum NotificationFilter {
  ALL = 'ALL',
  UNREAD = 'UNREAD',
  CRITICAL = 'CRITICAL',
  WARNING = 'WARNING',
  INFO = 'INFO',
}

export type NotificationAction = {
  label: string;
  route?: string;
  action?: string;
};

export type Notification = {
  notificationID: number;
  notificationType: string;
  priority: string;
  category: string;
  title: string;
  description: string;
  metadata?: Record<string, unknown> | null;
  isRead: boolean;
  isDismissed: boolean;
  createdUtc: string;
  readUtc?: string | null;
  dismissedUtc?: string | null;
  actions?: NotificationAction[];
};

export type GroupedNotification = {
  notification: Notification;
  groupCount: number;
  groupedNotifications: Notification[];
  isGrouped: boolean;
};

export enum DateCategory {
  NEW = 'NEW',
  YESTERDAY = 'YESTERDAY',
  EARLIER_THIS_WEEK = 'EARLIER_THIS_WEEK',
  LAST_WEEK = 'LAST_WEEK',
  EARLIER = 'EARLIER',
}

export type DateGroupedNotifications = {
  category: DateCategory;
  label: string;
  count: number;
  notifications: GroupedNotification[];
};

export type NotificationHubInfo = {
  title: string;
  description: string;
  category: NotificationCategory;
  notificationType: NotificationType;
  priority: NotificationPriority;
  actions?: NotificationAction[];
  metadataJson?: string | null;
};

export type NotificationHubState = {
  isOpen: boolean;
  notifications: Notification[];
  activeFilter: NotificationFilter;
  unreadCount: number;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
  isLoading: boolean;
  hasError: boolean;
};
